import { useMemo } from "react";
import TableColumnTitle from "components/TableColumnTitle/TableColumnTitle";
import { BulbOutlined, WalletOutlined } from "@ant-design/icons";
import { Descriptions, Typography, Tooltip } from "antd";
import { formatDateTime, renderUpdatedAt } from "helpers/generalHelpers";
import QuantityIncreaser from "components/GridView/QuantityIncreaser/QuantityIncreaser";
import { CircleIcon, CheckCircleIcon, CheckSquareIcon, WarningTriangleIcon } from "components/Icons";
import { changeQuantity } from "helpers/drawerHelpers";
import { getAssetDefinition } from "helpers/hierarchyHelpers";

const VALIDATION_STATUS_MAP = {
  'Not Validated': <CircleIcon className="validation-asset-not-validated-icon" />,
  'Validated': <CheckCircleIcon className="validation-asset-validated-icon" />,
  'Partial Validation': <CheckCircleIcon className="validation-asset-partially-validated-icon" />,
  'Unable to Locate': <WarningTriangleIcon className="validation-asset-not-located-icon" />,
  'New Asset': <CheckSquareIcon className="validation-asset-new-icon" />
}

const VALIDATION_PRIORITY_ORDER_MAP = {
  'New Asset': 1,
  'Validated': 2,
  'Partial Validation': 3,
  'Unable to Locate': 4,
  '': 5, // Excel import sets validation-status to empty string in facets where no value is recorded (denotes 'Not Validated')
}

function sortNullAlwaysLast(a, b, ascending = true) {
  if (a === null) {
    return ascending ? 1 : -1;
  }
  if (b === null) {
    return ascending ? -1 : 1;
  }
  if (a === b) {
    return 0;
  }

  return a < b ? -1 : 1;
}

export const ASSET_COLUMN = ({
  showAssetValidationStatus,
  tableColumnTitleProps,
  sortBy,
  sortOrder,
  actions
}) => {
  const validatedStatusColumn = [
    {
      title: <TableColumnTitle {...tableColumnTitleProps} dataIndex="validated" />,
      dataIndex: "validated",
      key: "Status",
      width: 40,
      fixed: "left",
      render: (_name, asset) => {
        const { facets: { 'validation-status': validationStatus = '' } = {} } = asset

        return (
        <Tooltip
          title={validationStatus !== '' ? validationStatus : 'Not Validated'}
          placement="bottom"
        >
          <div>{validationStatus !== '' ? VALIDATION_STATUS_MAP[validationStatus] : VALIDATION_STATUS_MAP['Not Validated']}</div>
        </Tooltip>
      )},
      sorter: (a, b) => {
        const { facets: { 'validation-status': validationStatusA = ''} = {} } = a
        const { facets: { 'validation-status': validationStatusB = ''} = {} } = b

        return VALIDATION_PRIORITY_ORDER_MAP[validationStatusA] < VALIDATION_PRIORITY_ORDER_MAP[validationStatusB] ? -1 : 1
      },
      sortOrder: sortBy === "validated" ? sortOrder : false,
    }
  ]

  const defaultColumns = [
    {
      title: <TableColumnTitle {...tableColumnTitleProps} dataIndex="name" />,
      dataIndex: "name",
      key: "Name",
      width: 200,
      fixed: "left",
      render: (name, asset) => (
        <>
          {asset.assetType.virtual === true ? (
            <BulbOutlined />
          ) : (
            <WalletOutlined />
          )}{" "}
          <>{name}</>
          <br />
          {asset.notes?.description && (
            <Typography.Text
              style={{ maxWidth: 200 }}
              ellipsis={{
                tooltip: true,
              }}
            >
              {asset.notes?.description || null}
            </Typography.Text>
          )}
        </>
      ),
      sorter: (a, b) =>
        a.name?.toLowerCase() < b.name?.toLowerCase() ? -1 : 1,
      sortOrder: sortBy === "name" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="barcode" />
      ),
      key: "Barcode",
      dataIndex: "barcode",
      sorter: (a, b) => {
        const aa = a.barcode ? `${a.barcode}`.toLowerCase() : null
        const bb = b.barcode ? `${b.barcode}`.toLowerCase() : null
        return sortNullAlwaysLast(aa, bb, sortOrder === 'ascend' ? true : false)
      },
      sortOrder: sortBy === "barcode" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="manufacturer" />
      ),
      key: "Manufacturer",
      dataIndex: "manufacturer",
      render: (manufacturer, asset) => (
        <>
          <>{manufacturer}</>
          <br />
          {asset.facets?.model && (
            <Typography.Text
              style={{ maxWidth: 200 }}
              ellipsis={{
                tooltip: true,
              }}
            >
              {asset.facets?.model || null}
            </Typography.Text>
          )}
        </>
      ),
      sorter: (a, b) => {
        const aa = a.manufacturer ? `${a.manufacturer}`.toLowerCase() : null
        const bb = b.manufacturer ? `${b.manufacturer}`.toLowerCase() : null
        return sortNullAlwaysLast(aa, bb, sortOrder === 'ascend' ? true : false)
      },
      sortOrder: sortBy === "manufacturer" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="installDate" />
      ),
      key: "Install Date",
      dataIndex: "installDate",
      render: (installDate) => {
        const yearOnly = new Date(installDate).getUTCFullYear();
        if (!installDate) return "";
        return yearOnly;
      },
      sorter: (a, b) => (a.installDate < b.installDate ? -1 : 1),
      sortOrder: sortBy === "installDate" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="condition" />
      ),
      key: "Condition",
      dataIndex: "condition",
      sorter: (a, b) => (a.condition < b.condition ? -1 : 1),
      sortOrder: sortBy === "condition" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="quantity" />
      ),
      key: "Quantity",
      dataIndex: "quantity",
      sorter: (a, b) => (a.quantity < b.quantity ? -1 : 1),
      sortOrder: sortBy === "quantity" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="updatedAt" />
      ),
      key: "Last Edited",
      dataIndex: "updatedAt",
      render: (_, item) => {
        return renderUpdatedAt(item);
      },
      sorter: (a, b) => (a.updatedAt < b.updatedAt ? -1 : 1),
      sortOrder: sortBy === "updatedAt" ? sortOrder : false,
    },
    {
      title: "",
      fixed: "right",
      render: (asset) => {
        return <div className="row-actions">{actions(asset)}</div>;
      },
    },
  ];

  return showAssetValidationStatus ? [ ...validatedStatusColumn, ...defaultColumns ] : defaultColumns;
};

export const ALL_ASSETS_COLUMN = ({
  showAssetValidationStatus,
  tableColumnTitleProps,
  sortBy,
  sortOrder,
  actions
}) => {
  const validatedStatusColumn = [
    {
      title: <TableColumnTitle {...tableColumnTitleProps} dataIndex="validated" />,
      dataIndex: "validated",
      key: "Status",
      width: 40,
      fixed: "left",
      render: (_name, asset) => {
        const { facets: { 'validation-status': validationStatus = '' } = {} } = asset

        return (
        <Tooltip
          title={validationStatus !== '' ? validationStatus : 'Not Validated'}
          placement="bottom"
        >
          <div>{validationStatus !== '' ? VALIDATION_STATUS_MAP[validationStatus] : VALIDATION_STATUS_MAP['Not Validated']}</div>
        </Tooltip>
      )},
      sorter: (a, b) => {
        const { facets: { 'validation-status': validationStatusA = ''} = {} } = a
        const { facets: { 'validation-status': validationStatusB = ''} = {} } = b

        return VALIDATION_PRIORITY_ORDER_MAP[validationStatusA] < VALIDATION_PRIORITY_ORDER_MAP[validationStatusB] ? -1 : 1
      },
      sortOrder: sortBy === "validated" ? sortOrder : false,
    }
  ]

  const defaultColumns = [
    {
      title: <TableColumnTitle {...tableColumnTitleProps} dataIndex="name" />,
      dataIndex: "name",
      key: "Name",
      width: 200,
      fixed: "left",
      render: (name, asset) => (
        <>
          {asset.assetType.virtual === true ? (
            <BulbOutlined />
          ) : (
            <WalletOutlined />
          )}{" "}
          <>{name}</>
          <br />
          {asset.notes?.description && (
            <Typography.Text
              style={{ maxWidth: 200 }}
              ellipsis={{
                tooltip: true,
              }}
            >
              {asset.notes?.description || null}
            </Typography.Text>
          )}
        </>
      ),
      sorter: (a, b) =>
        a.name?.toLowerCase() < b.name?.toLowerCase() ? -1 : 1,
      sortOrder: sortBy === "name" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="location" />
      ),
      key: "Location",
      dataIndex: "location",
      width: 200,
      sorter: (a, b) => {
        const { location:
          { 
            facilityName: facilityNameA = '', 
            floorName: floorNameA = '',
            spaceName: spaceNameA =''
          }
        } = a

        const { location:
          {
            facilityName: facilityNameB = '', 
            floorName: floorNameB = '',
            spaceName: spaceNameB = ''
          }
        } = b

        const locA = `${facilityNameA.trim()}/${floorNameA.trim()}/${spaceNameA.trim()}`.toLowerCase()
        const locB = `${facilityNameB.trim()}/${floorNameB.trim()}/${spaceNameB.trim()}`.toLowerCase()

        const aa = locA !== '//' ? locA : null
        const bb = locB !== '//' ? locB : null

        return sortNullAlwaysLast(aa, bb, sortOrder === 'ascend' ? true : false)
      },
      sortOrder: sortBy === "location" ? sortOrder : false,
      render: (location) => {
        const { facilityName, floorName, spaceName } = location 

        const assetLocation = `
          ${facilityName !== '' ? facilityName + ' / ' : null}
          ${floorName !== '' ? floorName + ' / ' : null}
          ${spaceName !== '' ? spaceName : null}
        `
        return (
          <Tooltip title={assetLocation}>
            <div className="all-assets-locations-column">{facilityName}</div>
            <div className="all-assets-locations-column">{floorName}</div>
            <div className="all-assets-locations-column">{spaceName}</div>
          </Tooltip>
        )
      }
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="barcode" />
      ),
      key: "Barcode",
      dataIndex: "barcode",
      sorter: (a, b) => {
        const aa = a.barcode ? `${a.barcode}`.toLowerCase() : null
        const bb = b.barcode ? `${b.barcode}`.toLowerCase() : null
        return sortNullAlwaysLast(aa, bb, sortOrder === 'ascend' ? true : false)
      },
      sortOrder: sortBy === "barcode" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="manufacturer" />
      ),
      key: "Manufacturer",
      dataIndex: "manufacturer",
      render: (manufacturer, asset) => (
        <>
          <>{manufacturer}</>
          <br />
          {asset.facets?.model && (
            <Typography.Text
              style={{ maxWidth: 200 }}
              ellipsis={{
                tooltip: true,
              }}
            >
              {asset.facets?.model || null}
            </Typography.Text>
          )}
        </>
      ),
      sorter: (a, b) => {
        const aa = a.manufacturer ? `${a.manufacturer}`.toLowerCase() : null
        const bb = b.manufacturer ? `${b.manufacturer}`.toLowerCase() : null
        return sortNullAlwaysLast(aa, bb, sortOrder === 'ascend' ? true : false)
      },
      sortOrder: sortBy === "manufacturer" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="installDate" />
      ),
      key: "Install Date",
      dataIndex: "installDate",
      render: (installDate) => {
        const yearOnly = new Date(installDate).getUTCFullYear();
        if (!installDate) return "";
        return yearOnly;
      },
      sorter: (a, b) => (a.installDate < b.installDate ? -1 : 1),
      sortOrder: sortBy === "installDate" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="condition" />
      ),
      key: "Condition",
      dataIndex: "condition",
      sorter: (a, b) => (a.condition < b.condition ? -1 : 1),
      sortOrder: sortBy === "condition" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="quantity" />
      ),
      key: "Quantity",
      dataIndex: "quantity",
      sorter: (a, b) => (a.quantity < b.quantity ? -1 : 1),
      sortOrder: sortBy === "quantity" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="updatedAt" />
      ),
      key: "Last Edited",
      dataIndex: "updatedAt",
      width: 200,
      render: (_, item) => {
        return renderUpdatedAt(item, true);
      },
      sorter: (a, b) => (a.updatedAt < b.updatedAt ? -1 : 1),
      sortOrder: sortBy === "updatedAt" ? sortOrder : false,
    },
    {
      title: "",
      fixed: "right",
      render: (asset) => {
        return <div className="row-actions">{actions(asset)}</div>;
      },
    },
  ]

  return showAssetValidationStatus ? [ ...validatedStatusColumn, ...defaultColumns ] : defaultColumns;
};

export const useRenderGrid = (asset) => {
  const assetDefinition = useMemo(
    () => getAssetDefinition(asset.assetType.eliasID, window.hierarchy),
    [asset.assetType.eliasID]
  );

  return (
    <Descriptions
      column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
      size={"small"}
    >
      <Descriptions.Item label="Quantity" labelStyle={{ margin: "auto" }}>
        {asset.assetType.virtual === true ||
          assetDefinition?.allowMultiple ? (
          <QuantityIncreaser
            inputValue={asset.facets.quantity}
            onChange={(qty) => changeQuantity(qty, asset.id)}
          />
        ) : (
          asset.facets.quantity
        )}
      </Descriptions.Item>
      {asset.facets["barcode"] && (
        <Descriptions.Item label="Barcode">
          {asset.facets.barcode}
        </Descriptions.Item>
      )}
      <Descriptions.Item label="Install Date">
        {formatDateTime(asset.facets["install-date"])}
      </Descriptions.Item>
      {asset.facets["condition"] &&
        typeof asset.facets["condition"] === "string" && (
          <Descriptions.Item label="Condition">
            <Tooltip title={asset.notes?.condition}>
              {asset.facets?.condition}
            </Tooltip>
          </Descriptions.Item>
        )}
      {asset.facets["manufacturer"] && (
        <Descriptions.Item label="Manufacturer">
          {asset.facets["manufacturer"]}
        </Descriptions.Item>
      )}
      {asset.facets["model"] && (
        <Descriptions.Item label="Model">
          {asset.facets["model"]}
        </Descriptions.Item>
      )}
      {asset && (
        <Descriptions.Item label="Last Edited">
          {renderUpdatedAt(asset)}
        </Descriptions.Item>
      )}
    </Descriptions>
  );
};